/**
 * @fullvsl/app-shell/views/templates — overlay template gallery (FUL-272).
 *
 * Read-only overlay presets for the MVP player moments. Presets are copied into
 * each VSL at creation; customization happens only inside that VSL's editor.
 *
 * Templates come from the shared contract library (overlay-templates.ts) so the
 * editor, preview, and player all read the same defaults.
 */
import type { OverlayType } from "../../../contracts/src/contracts";
import { OVERLAY_TEMPLATES } from "../../../contracts/src/overlay-templates";
import { el, append } from "../dom";

const TEMPLATE_META: Record<
  OverlayType,
  { title: string; when: string; description: string }
> = {
  start: {
    title: "Início",
    when: "Antes da primeira reprodução",
    description: "Overlay de convite exibido quando a reprodução automática está desativada ou bloqueada.",
  },
  autoplay: {
    title: "Reprodução automática",
    when: "Reprodução automática sem som",
    description: "Convida o espectador a tocar para ativar o som.",
  },
  minihook: {
    title: "Gancho durante o vídeo",
    when: "Em um momento do vídeo",
    description: "Mensagem discreta exibida em um momento configurado do vídeo.",
  },
  pause: {
    title: "Pausa",
    when: "Ao pausar (após 10s)",
    description: "Oferece ações para o espectador quando o vídeo é pausado.",
  },
  cta_reveal: {
    title: "Exibição da CTA",
    when: "Ao atingir o progresso configurado",
    description: "Personaliza a chamada para ação exibida dentro do player.",
  },
  end: {
    title: "Final",
    when: "Ao concluir",
    description: "Chamada final de conversão exibida ao término do vídeo.",
  },
};

const TEMPLATE_ORDER: OverlayType[] = [
  "start",
  "autoplay",
  "minihook",
  "pause",
  "cta_reveal",
  "end",
];

export function renderTemplatesView(navigate: (path: string) => void): HTMLElement {
  const wrapper = el("div");

  // ---- Page header ----
  const header = el("div", "fva-page-header");
  const headerText = el("div");
  append(headerText, el("h2", null, "Modelos de overlay"));
  append(headerText, el("p", null, "Modelos reutilizáveis, copiados de forma independente para cada nova VSL."));
  const openVsls = el("button", "fva-btn fva-btn-primary", "Personalizar uma VSL") as HTMLButtonElement;
  openVsls.addEventListener("click", () => navigate("#/"));
  append(header, headerText, openVsls);
  append(wrapper, header);

  const banner = el("div", "fva-info-banner");
  banner.appendChild(
    document.createTextNode(
      "Os modelos não são compartilhados entre VSLs. Abra uma VSL e use a aba Overlays para personalizar texto, momento, aparência e ações somente daquele projeto.",
    ),
  );
  append(wrapper, banner);

  const grid = el("div", "fva-tpl-grid");
  for (const type of TEMPLATE_ORDER) {
    grid.appendChild(renderTemplateCard(type));
  }
  append(wrapper, grid);

  return wrapper;
}

function renderTemplateCard(type: OverlayType): HTMLElement {
  const meta = TEMPLATE_META[type];
  const tmpl = OVERLAY_TEMPLATES[type];
  const card = el("div", "fva-card fva-card-pad fva-tpl-card");

  // Card header
  const head = el("div", "fva-tpl-head");
  const headLeft = el("div");
  append(headLeft, el("h3", "fva-tpl-title", meta.title));
  append(headLeft, el("span", "fva-tpl-when", meta.when));
  append(head, headLeft);
  append(head, el("span", "fva-tag", formatAnimation(tmpl.content.animation ?? "fade")));
  append(card, head);
  append(card, el("p", "fva-tpl-desc", meta.description));

  // Publicada preview frame
  const preview = el("div", "fva-tpl-preview");
  const overlayEl = el("div", "fva-tpl-overlay");
  positionOverlay(overlayEl, tmpl.content.position ?? "center");
  paintOverlay(overlayEl, tmpl);
  preview.appendChild(overlayEl);
  const playGlyph = el("div", "fva-tpl-preview-play", "▶");
  preview.appendChild(playGlyph);
  append(card, preview);

  append(card, el("p", "fva-tpl-preset-note", "Copiado para cada VSL como configuração independente."));

  // Metadata footer (timing + actions)
  const footer = el("div", "fva-tpl-meta");
  const timingLabel = formatTiming(tmpl.timing.show, tmpl.timing.at);
  append(footer, el("span", "fva-tpl-meta-label", `Momento: ${timingLabel}`));
  if (tmpl.actions.length > 0) {
    append(footer, el("span", "fva-tpl-meta-label", `Ações: ${tmpl.actions.map(formatAction).join(", ")}`));
  }
  append(card, footer);

  return card;
}

/** Position the overlay node inside the preview frame. */
function positionOverlay(node: HTMLElement, position: string): void {
  node.style.alignItems = "center";
  node.style.justifyContent = "center";
  if (position === "top") node.style.alignItems = "flex-start";
  else if (position === "bottom") node.style.alignItems = "flex-end";
  else if (position === "top_left" || position === "bottom_left") node.style.justifyContent = "flex-start";
  else if (position === "top_right" || position === "bottom_right") node.style.justifyContent = "flex-end";
  if (position === "top_left" || position === "top_right") node.style.alignItems = "flex-start";
  if (position === "bottom_left" || position === "bottom_right") node.style.alignItems = "flex-end";
}

/** Render the overlay content (headline/subtext/button) into the preview node. */
function paintOverlay(node: HTMLElement, tmpl: typeof OVERLAY_TEMPLATES[OverlayType]): void {
  node.innerHTML = "";
  const opacity = tmpl.content.bgOpacity ?? 0.55;
  const colors = tmpl.content.colors ?? {};
  node.style.background =
    colors.background ?? `rgba(0,0,0,${opacity})`;
  node.style.color = colors.text ?? "#ffffff";

  const panel = el("div", "fva-tpl-overlay-panel");
  if (tmpl.content.text) {
    append(panel, el("p", "fva-tpl-overlay-text", tmpl.content.text));
  }
  if (tmpl.content.subtext) {
    append(panel, el("p", "fva-tpl-overlay-sub", tmpl.content.subtext));
  }
  if (tmpl.content.buttonLabel) {
    const btn = el("span", "fva-tpl-overlay-btn", tmpl.content.buttonLabel) as HTMLElement;
    btn.style.background = colors.button ?? "#00685f";
    btn.style.color = colors.buttonText ?? "#ffffff";
    panel.appendChild(btn);
  }
  node.appendChild(panel);
}

function formatTiming(show: string, at?: number): string {
  switch (show) {
    case "immediate":
      return "Imediato";
    case "timestamp":
      return at != null ? `Em ${at}s` : "Em um momento específico";
    case "duration":
      return "Por uma duração";
    case "onPause":
      return "Ao pausar";
    case "onReplay":
      return "Ao concluir";
    default:
      return show;
  }
}

function formatAnimation(animation: string): string {
  return ({ none: "Nenhuma", fade: "Suave", slide: "Deslizar", zoom: "Zoom" } as Record<string, string>)[animation] ?? animation;
}

function formatAction(action: string): string {
  return ({
    continue: "continuar",
    restart: "reiniciar",
    jump_to_offer: "ir para a oferta",
    open_cta: "abrir CTA",
    close: "fechar",
    unmute: "ativar som",
  } as Record<string, string>)[action] ?? action.replace(/_/g, " ");
}
